import { useState } from 'react';
import type { Locale } from 'react-day-picker';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { Button } from '../button';
import { Calendar } from '../calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { cn } from '../../lib/utils';
import { XIcon } from 'lucide-react';

export type DateFieldTexts = {
	pickDate: string;
	clearDate: string;
};

const defaultTexts: DateFieldTexts = {
	pickDate: 'Pick a date',
	clearDate: 'Clear date',
};

// Format Date → YYYY-MM-DD string
const dateToString = (date: Date) => {
	const y = date.getFullYear();
	const m = (date.getMonth() + 1).toString().padStart(2, '0');
	const d = date.getDate().toString().padStart(2, '0');
	return `${y}-${m}-${d}`;
};

export default function DateField({
	label,
	className,
	orientation,
	texts: textsProp,
	locale,
}: {
	label: string;
	className?: string;
	orientation?: 'vertical' | 'horizontal';
	texts?: Partial<DateFieldTexts>;
	locale?: Partial<Locale>;
}) {
	const texts = { ...defaultTexts, ...textsProp };
	const field = useFieldContext<string | null | undefined>();
	const formReadOnly = useFormReadOnly();
	const [open, setOpen] = useState(false);
	const isHorizontal = orientation === 'horizontal';
	const isOptional = useIsFieldOptional(field.name);

	// Convert YYYY-MM-DD or ISO datetime string → Date (avoid timezone offset)
	const rawValue = field.state.value;
	const dateStr = rawValue?.includes('T') ? rawValue.split('T')[0] : rawValue;
	const dateValue = dateStr ? new Date(dateStr + 'T00:00:00') : undefined;

	return (
		<div className={cn(className, formReadOnly && 'pointer-events-none')}>
			<div className={isHorizontal ? 'flex items-start gap-3' : 'grid gap-2'}>
				<FieldLabelRow
					label={label}
					htmlFor={field.name}
					hasError={field.state.meta.isTouched && !field.state.meta.isValid}
					optional={isOptional}
					className={cn('flex items-center justify-between', isHorizontal && 'w-36 shrink-0')}
				/>
				<div className={isHorizontal ? 'flex-1' : undefined}>
					<div className="relative">
						<Popover open={!formReadOnly && open} onOpenChange={setOpen}>
							<PopoverTrigger asChild>
								<Button
									id={field.name}
									aria-invalid={hasFieldError(field.state.meta)}
									variant="outline"
									data-empty={!dateValue}
									tabIndex={formReadOnly ? -1 : undefined}
									className={cn(
										'w-full justify-start text-left font-normal transition-[color,border-color,padding,box-shadow] data-[empty=true]:text-muted-foreground',
										formReadOnly && 'pointer-events-none border-transparent px-0 shadow-none',
									)}
								>
									{dateValue ? dateValue.toLocaleDateString(locale?.code) : formReadOnly ? '-' : texts.pickDate}
								</Button>
							</PopoverTrigger>
							<PopoverContent className="w-auto overflow-hidden p-0" align="start">
								<Calendar
									mode="single"
									locale={locale}
									selected={dateValue}
									defaultMonth={dateValue}
									captionLayout="dropdown"
									startMonth={new Date(1920, 0)}
									endMonth={new Date(2100, 11)}
									onSelect={(date) => {
										field.handleChange(date ? dateToString(date) : null);
										setOpen(false);
									}}
								/>
							</PopoverContent>
						</Popover>
						{dateValue && (
							<div
								className={cn(
									'absolute top-1/2 right-2 -translate-y-1/2 transition-[opacity,transform] duration-200',
									formReadOnly ? 'pointer-events-none scale-0 opacity-0' : 'scale-100 opacity-100',
								)}
							>
								<button
									type="button"
									onClick={() => field.handleChange(null)}
									className="cursor-pointer rounded-full bg-black/50 p-0.5 text-white transition-colors hover:bg-black/70"
								>
									<XIcon className="size-3" />
									<span className="sr-only">{texts.clearDate}</span>
								</button>
							</div>
						)}
					</div>
					<FieldInfo fieldState={field.state} />
				</div>
			</div>
		</div>
	);
}
